import {getTestProps} from '@benchling/js-test-utils';
import {CloseButton, createStyles, Group, Loader, Stack, Text} from '@mantine/core';
import React, {ChangeEvent, useCallback, useRef, useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import DropTarget from 'util/drag-and-drop/DropTarget';
import {getUserMessage} from 'util/Net';

type UploadState = 'idle' | 'uploading' | 'error';

interface UploadSkillModalProps {
  onRequestClose: () => void;
  onRequestSubmit?: (files: Array<File>) => void | Promise<void>;
}

function isSkillFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.zip') || name.endsWith('.skill');
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing.sm,
    padding: `${theme.spacing.xs} 0`,
  },
  errorBox: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.red[6]}`,
    backgroundColor: theme.colors.red[0],
    color: theme.colors.red[8],
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.5,
  },
  dropZone: {
    minHeight: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px dashed ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.md,
    cursor: 'pointer',
    padding: theme.spacing.md,
  },
  fileListContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing.xs,
    maxHeight: 200,
    overflowY: 'auto' as const,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: `4px ${theme.spacing.xs}`,
    borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.gray[3]}`,
    backgroundColor: theme.colors.gray[0],
  },
  uploadIcon: {
    fontSize: 24,
    color: theme.colors.gray[6],
  },
  fileIcon: {
    fontSize: 16,
    color: theme.colors.gray[7],
    flexShrink: 0,
  },
  fileName: {
    flex: 1,
    minWidth: 0,
  },
}));

export default function UploadSkillModal({
  onRequestClose,
  onRequestSubmit,
}: UploadSkillModalProps): JSX.Element {
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([]);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {classes} = useStyles();

  const addFiles = useCallback((files: Array<File>) => {
    const valid: Array<File> = [];
    const invalid: Array<string> = [];
    for (const file of files) {
      if (isSkillFile(file)) {
        valid.push(file);
      } else {
        invalid.push(file.name);
      }
    }
    if (invalid.length > 0) {
      setErrorMessage(
        `Skipped ${invalid.length} non-skill file(s). Only .zip and .skill files are accepted.`
      );
    } else {
      setErrorMessage(null);
    }
    if (valid.length > 0) {
      setSelectedFiles((prev) => {
        const existingNames = new Set(prev.map((f) => f.name));
        const newFiles = valid.filter((f) => !existingNames.has(f.name));
        return [...prev, ...newFiles];
      });
    }
  }, []);

  const removeFile = useCallback((fileName: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      const droppedFiles = e.dataTransfer?.files;
      if (droppedFiles && droppedFiles.length > 0) {
        addFiles(Array.from(droppedFiles));
      }
    },
    [addFiles]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const files = e.target.files;
      if (files && files.length > 0) {
        addFiles(Array.from(files));
      }
      e.target.value = '';
    },
    [addFiles]
  );

  async function handleSubmit(): Promise<void> {
    if (selectedFiles.length === 0 || uploadState === 'uploading') {
      return;
    }
    setUploadState('uploading');
    setErrorMessage(null);
    try {
      if (onRequestSubmit) {
        await onRequestSubmit(selectedFiles);
      }
      onRequestClose();
    } catch (err: unknown) {
      setUploadState('error');
      const serverMessage = getUserMessage(err);
      if (serverMessage) {
        setErrorMessage(serverMessage);
      } else if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        throw err;
      }
    }
  }

  const uploadLabel = selectedFiles.length <= 1 ? 'Upload Skill' : `Upload ${selectedFiles.length} Skills`;

  const buttons: Array<ModalButton> = [
    {text: 'Cancel', onClick: onRequestClose},
    {
      text: uploadLabel,
      submit: true,
      style: 'primary',
      disabled: selectedFiles.length === 0 || uploadState === 'uploading',
    },
  ];

  return (
    <Modal
      title="Upload Skills"
      buttons={buttons}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
    >
      <div className={classes.container}>
        {errorMessage != null && (
          <div className={classes.errorBox}>
            <Text fw="bold" size="sm" c="inherit" mb={4}>
              Error
            </Text>
            {errorMessage}
          </div>
        )}

        <div>
          <Text size="sm" c="dimmed" mb="xs">
            Upload one or more Skill archives (.zip or .skill) containing a SKILL.md file with YAML
            frontmatter.
          </Text>

          <DropTarget
            shouldCaptureDrop={true}
            mimeTypes={['Files']}
            dropEffect="copy"
            targetDesc=".skill or .zip files"
            onDrop={handleDrop}
          >
            <div
              role="button"
              tabIndex={0}
              className={classes.dropZone}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              {uploadState === 'uploading' ? (
                <Group spacing="xs">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Uploading {selectedFiles.length === 1 ? 'skill' : `${selectedFiles.length} skills`}...
                  </Text>
                </Group>
              ) : (
                <Stack align="center" spacing={4}>
                  <BenchlingIcon icon="upload" className={classes.uploadIcon} />
                  <Text size="sm" c="dimmed">
                    Drop .zip or .skill files here, or click to select
                  </Text>
                </Stack>
              )}
            </div>
          </DropTarget>
          <input
            {...getTestProps({component: 'UploadSkillModal', element: 'fileInput'})}
            type="file"
            accept=".zip,.skill"
            multiple={true}
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileInputChange}
          />
        </div>

        {selectedFiles.length > 0 && (
          <div className={classes.fileListContainer}>
            {selectedFiles.map((file) => (
              <div key={file.name} className={classes.fileRow}>
                <BenchlingIcon icon="file" className={classes.fileIcon} />
                <Text size="sm" truncate={true} className={classes.fileName}>
                  {file.name}
                </Text>
                <CloseButton
                  size="xs"
                  onClick={() => {
                    removeFile(file.name);
                  }}
                  aria-label={`Remove ${file.name}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
